import { formatNumber } from 'utils/format-number'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import 'dayjs/locale/en'
import marketService from 'services/market-service'
import { MarketOffer, MarketOffers } from 'models/market'
import { getLangFromUrl } from 'utils/current-language'

dayjs.extend(relativeTime)

declare const USER_BASE_URL_WT: string
declare let SINGLE_RATE_BASE_CURRENCY: string
declare let SINGLE_RATE_COUNTER_CURRENCY: string

export interface CurrencyPair {
  base: string
  counter: string
  merge: string
}

type DataDirection = 'ASK' | 'BID'
type DataTypeUpdate = 'FULL' | 'ASK' | 'BID'
type TablesScopeInfo = {
  partialScope: DataDirection[]
  fullScope: DataDirection[]
}

const changeFetchScope = (button: HTMLButtonElement, type: DataDirection): void => {
  button.dataset['refresh'] = 'full'
  updateGUI(type, true, true)
}

const bindButtonActions = (): void => {
  const $buttons = document.querySelectorAll<HTMLButtonElement>('[data-populate-items]')
  $buttons.forEach(($button) => {
    $button.addEventListener('click', () => {
      changeFetchScope($button, $button.getAttribute('data-populate-items').toUpperCase() as DataDirection)
    })
  })
}

const cleanupTableContent = (type: DataDirection): void => {
  const $tableElementsToDrop = document.querySelectorAll<HTMLTableElement>(
    `[data-populate-offers-table='${type.toLowerCase()}'] tbody tr:not([data-button-tablerow])`,
  )

  $tableElementsToDrop.forEach(($singleItem) => {
    if ($singleItem.parentNode) $singleItem.parentNode.removeChild($singleItem)
  })
}

const updateStatisticInfo = (
  data: MarketOffers,
  type: DataDirection,
  isFullScopeUpdate: boolean,
  rawDatakey: string,
): void => {
  const $offersTableElement = document.querySelector(`[data-offers-table='${type}']`)
  let offerCount = 0

  if ($offersTableElement) {
    if (isFullScopeUpdate) {
      offerCount = data[rawDatakey].length
    } else {
      offerCount = data['offer_count'] && data['offer_count'][rawDatakey] ? data['offer_count'][rawDatakey] : 0
    }
    $offersTableElement.textContent = offerCount.toString()
  }
}

const createRow = (data: MarketOffer, type: DataDirection): HTMLTableRowElement => {
  const pageLang = getLangFromUrl()
  const $newRow = document.createElement('tr')
  $newRow.classList.add('data-table__table-row')

  const displayCurrency: string = type === 'BID' ? SINGLE_RATE_COUNTER_CURRENCY : SINGLE_RATE_BASE_CURRENCY
  const displaySingleAmount: string = type === 'BID' ? 'counter_amount' : 'amount'

  const $newTableCellRate: HTMLTableCellElement = document.createElement('td')
  $newTableCellRate.classList.add('data-table__table-row-cell', 'is-rate-actual')
  $newTableCellRate.textContent = formatNumber(data.rate, 4)

  const $newTableCellAmount: HTMLTableCellElement = document.createElement('td')
  $newTableCellAmount.classList.add('data-table__table-row-cell', 'is-turnover-actual')
  $newTableCellAmount.textContent = `${formatNumber(data[displaySingleAmount], 2)} ${displayCurrency}`

  const $newTableCellDate: HTMLTableCellElement = document.createElement('td')
  $newTableCellDate.classList.add('data-table__table-row-cell', 'is-since-actual')

  const timeFromNow = dayjs(data.since)
    .locale(pageLang === 'pl' ? 'pl' : 'en')
    .fromNow()

  $newTableCellDate.textContent = timeFromNow

  $newRow.appendChild($newTableCellRate)
  $newRow.appendChild($newTableCellAmount)
  $newRow.appendChild($newTableCellDate)

  return $newRow
}

const populateTable = (
  type: DataDirection,
  data: MarketOffers,
  scrollToTop: boolean,
  isFullScopeUpdate = false,
  currencyPair: string,
): void => {
  cleanupTableContent(type)
  const rawDatakey: string = `${type}_${currencyPair}`
  const rawData = !data[rawDatakey] ? [] : data[rawDatakey].reverse()
  const $tableParent = document.querySelector<HTMLTableElement>(
    `[data-populate-offers-table='${type.toLowerCase()}'] tbody`,
  )
  const $noOffersElement = $tableParent?.querySelector<HTMLTableRowElement>(`[data-no-offers-${type.toLowerCase()}]`)
  const $showMoreElement = $tableParent?.querySelector<HTMLTableRowElement>(`[data-show-more-${type.toLowerCase()}]`)
  const $tableContainer = $tableParent?.closest<HTMLDivElement>('.actual-offers__table-container')

  updateStatisticInfo(data, type, isFullScopeUpdate, rawDatakey)
  if ($noOffersElement) $noOffersElement.hidden = rawData.length > 0
  if ($showMoreElement) $showMoreElement.hidden = !!(rawData.length < 15 || isFullScopeUpdate)

  rawData.forEach((singleRow) => {
    const $newRow = createRow(singleRow, type)
    $tableParent.prepend($newRow)
  })

  if ($tableContainer && scrollToTop) $tableContainer.scrollTop = 0
}

const updateGUI = (type: DataTypeUpdate, scrollToTop = false, isFullScopeUpdate: boolean = false): void => {
  const scope: string = isFullScopeUpdate ? '' : '?brief=true'
  const currencyPair: string = `${SINGLE_RATE_BASE_CURRENCY}_${SINGLE_RATE_COUNTER_CURRENCY}`

  marketService
    .fetchMarketPriceVolumes(USER_BASE_URL_WT, currencyPair, scope)
    .then((response) => {
      if (response && typeof response === 'object' && Object.keys(response).length > 0) {
        if (type === 'FULL') {
          populateTable('ASK', response, scrollToTop, isFullScopeUpdate, currencyPair)
          populateTable('BID', response, scrollToTop, isFullScopeUpdate, currencyPair)
        } else {
          populateTable(type, response, scrollToTop, isFullScopeUpdate, currencyPair)
        }
      } else {
        console.warn('No data to display')
      }
    })
    .catch((error) => {
      console.error('Error', error)
    })
}

const positionPopup = ($popupElement: HTMLSpanElement): void => {
  const $rect = $popupElement.getBoundingClientRect()
  const $rectWidth = $rect.width
  const $clientWidth = document.documentElement.clientWidth
  const $distanceFromRight = $clientWidth - $rect.right
  const $popupElementPosition = $rectWidth - 30

  if ($clientWidth > 401) {
    if ($distanceFromRight < 30) {
      $popupElement.style.left = `-${$popupElementPosition}px`
    } else if ($distanceFromRight > $rectWidth / 2 + 15) {
      $popupElement.style.left = ''
    }
  } else {
    $popupElement.style.left = ''
  }
}

const resizePopup = ($popupElement: HTMLSpanElement): void => {
  window.addEventListener('resize', function () {
    positionPopup($popupElement)
  })
}

export const serviceInfoPopup = ($holderElement: HTMLSpanElement, $infoElement: HTMLSpanElement): void => {
  if ($holderElement) {
    $holderElement.addEventListener('mouseover', () => {
      $infoElement.hidden = false
      positionPopup($infoElement)
    })

    $holderElement.addEventListener('mouseleave', () => {
      $infoElement.hidden = true
      $infoElement.style.left = ''
    })
  }
}

const initHelpTip = (): void => {
  const $signHolder = document.querySelectorAll<HTMLSpanElement>('.data-table__sign-holder')
  const $infoPopup = document.querySelectorAll<HTMLSpanElement>('.data-table__answer-info')
  const $infoPopupWrap = document.querySelector<HTMLSpanElement>('.actual-single-rate__answer-info')

  if ($infoPopupWrap) resizePopup($infoPopupWrap)

  serviceInfoPopup($signHolder[0], $infoPopup[0])
  serviceInfoPopup($signHolder[1], $infoPopup[1])
}

const checkTableScope = (): TablesScopeInfo => {
  const tablesScope: TablesScopeInfo = { partialScope: ['ASK', 'BID'], fullScope: [] }
  const $buttons = document.querySelectorAll<HTMLButtonElement>('[data-refresh="full"]')

  $buttons.forEach(($button) => {
    const dataDirection = $button.getAttribute('data-populate-items').toUpperCase()
    if (dataDirection === 'ASK' || dataDirection === 'BID') {
      tablesScope.fullScope.push(dataDirection)
      tablesScope.partialScope = tablesScope.partialScope.filter((item) => item !== dataDirection)
    }
  })

  return tablesScope
}

export const handleUpdateActualOffers = (): void => {
  const $actualOffersTable = document.querySelector<HTMLTableElement>('[data-populate-offers-table]')

  if ($actualOffersTable) {
    const { fullScope, partialScope } = checkTableScope()

    if (fullScope.length === 1) {
      updateGUI(fullScope[0], false, true)
      updateGUI(partialScope[0], false, false)
    } else {
      const isFullScopeUpdate = fullScope.length === 2
      updateGUI('FULL', false, isFullScopeUpdate)
    }
  }
}

export function initActualOffers(): void {
  if (document.querySelector('[data-populate-offers-table]')) {
    bindButtonActions()
    initHelpTip()
  }
}
